import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useRef,
} from "react";
import httpClient from "../utils/httpClient";
import { ThemeColors } from "../utils/themeContextHelper";
import { useAuth } from "@frontegg/react";
import { useLocation } from "react-router-dom";
import { appKeyForRoute } from "utils/apps";
import DefaultLightLogo from "../assets/images/OutpaveMain.svg";
import DefaultDarkLogo from "../assets/images/OutpaveSecondary.svg";
import CardIssuer from "enums/CardIssuer";
import { setBrandVars } from "../i8n";
import {
  readBrandingLogosCache,
  writeBrandingLogosCache,
} from "utils/branding/brandingCache";

/**
 * Platform metadata, logos, and runtime branding: the data half of the old
 * ThemeContext (D7). Writes no CSS variables (single-writer rule, R3).
 */

interface Logos {
  icon: string;
  /** The company's OWN card artwork, empty when they have not uploaded any. */
  customCardImage: string;
  /** Worn when a company uploads none of its own; empty when the platform has none either. */
  platformCardImage: string;
  logo: string;
  lightLogo: string;
  favicon: string;
}

export interface PlatformInfo {
  name: string;
  email: string;
  cardIssuer: CardIssuer;
  brandColors?: Partial<ThemeColors> | null;
  footerDisclosure?: string | null;
  termsUrl?: string | null;
  privacyUrl?: string | null;
  iosAppUrl?: string | null;
  androidAppUrl?: string | null;
  issuingBankName?: string | null;
  issuingBankDisclosure?: string | null;
  cardholderAgreementUrl?: string | null;
  platformAddress?: string | null;
  agreements?: Array<{
    key?: string;
    label: string;
    url?: string | null;
  }> | null;
  logos?: {
    logo?: string | null;
    lightLogo?: string | null;
    favicon?: string | null;
    cardImage?: string | null;
  } | null;
}

interface BrandingContextType {
  platform: PlatformInfo;
  brandColors: Partial<ThemeColors> | null;
  logos: Logos;
  updateBrandColors: (colors: Partial<ThemeColors>) => void;
  resetTheme: () => void;
  /** Resolves false when the request failed and the branding kept what it had. */
  refetchBranding: () => Promise<boolean>;
  isBrandingLoaded: boolean;
}

const TRANSPARENT =
  "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxIiBoZWlnaHQ9IjEiLz4=";

/** Nothing drawn yet. A wrong tenant's logo is worse than no logo. */
const BLANK_LOGOS: Logos = {
  icon: TRANSPARENT,
  logo: TRANSPARENT,
  lightLogo: TRANSPARENT,
  customCardImage: "",
  platformCardImage: TRANSPARENT,
  favicon: "",
};

/**
 * A platform record's card is an absolute URL, and "" means it has none. An older
 * build cached the bundled Outpave card here, which a build places under
 * /static/media/, so anything else waits for the fetch.
 */
const cachedPlatformCard = (value?: string) => {
  if (value === "") return "";
  const fromRecord =
    !!value && /^https?:\/\//i.test(value) && !value.includes("/static/media/");
  return fromRecord ? value : TRANSPARENT;
};

const logosFromCache = (cached: Partial<Logos> | null): Logos =>
  cached
    ? {
        icon: cached.icon || "",
        logo: cached.logo || DefaultDarkLogo,
        lightLogo: cached.lightLogo || DefaultLightLogo,
        customCardImage: cached.customCardImage || "",
        platformCardImage: cachedPlatformCard(cached.platformCardImage),
        favicon: cached.favicon || "",
      }
    : BLANK_LOGOS;

const DEFAULT_PLATFORM: PlatformInfo = {
  name: "",
  email: "support@outpave.com",
  cardIssuer: CardIssuer.STRIPE,
  brandColors: {},
};

const BrandingContext = createContext<BrandingContextType>({
  platform: DEFAULT_PLATFORM,
  brandColors: {} as Partial<ThemeColors> | null,
  logos: {
    icon: "",
    logo: DefaultDarkLogo,
    lightLogo: DefaultLightLogo,
    customCardImage: "",
    platformCardImage: "",
    favicon: "",
  },
  updateBrandColors: () => {},
  resetTheme: () => {},
  refetchBranding: async () => false,
  isBrandingLoaded: false,
});

export const useBrandingContext = () => useContext(BrandingContext);

export const BrandingProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [activeBrandColors, setActiveBrandColors] =
    useState<Partial<ThemeColors> | null>(null);

  const [platform, setPlatform] = useState<PlatformInfo>(DEFAULT_PLATFORM);

  // Hydrated by the effect below, once there is an identity to key the cache
  // by. Reading it at mount is what let one tenant's logos render for another.
  const [logos, setLogos] = useState<Logos>(BLANK_LOGOS);

  const [isBrandingFetched, setIsBrandingFetched] = useState<boolean>(false);
  const [isThemeReady, setIsThemeReady] = useState<boolean>(false);
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const isPlatformApp = appKeyForRoute(location.pathname) === "platform";
  const scopedLogos: Logos = isPlatformApp
    ? {
        ...logos,
        logo: platform?.logos?.logo || logos.logo,
        lightLogo: platform?.logos?.lightLogo || logos.lightLogo,
      }
    : logos;

  // ============================================
  //  FETCH BRANDING FROM BACKEND
  // ============================================

  // Every read and every local colour write takes the next number, so a read
  // that lands can tell what has happened since it left.
  const changes = useRef(0);
  const latestRead = useRef<{ seq: number; done: Promise<boolean> } | null>(
    null,
  );
  const colorsWrittenAt = useRef(0);

  /** The read that replaced this one, when a newer read has started since. */
  const newerRead = (seq: number) =>
    latestRead.current && latestRead.current.seq !== seq
      ? latestRead.current.done
      : null;

  const readBranding = async (seq: number): Promise<boolean> => {
    try {
      const response = await httpClient.get(
        `/custom-branding/${user?.id ?? ""}?_t=${Date.now()}`,
      );
      // An older read applies nothing, and answers with the read that replaced it.
      const newer = newerRead(seq);
      if (newer) return newer;
      const { platform, brandColors, logos } = response.data;

      setPlatform(platform);
      setBrandVars({
        platformName: platform?.name,
        supportEmail: platform?.email,
      });
      // Colours saved after this read left are newer than the ones it carries.
      if (colorsWrittenAt.current < seq) setActiveBrandColors(brandColors);
      const nextLogos = {
        icon: logos.icon || "",
        logo: logos.logo || DefaultDarkLogo,
        lightLogo: logos.lightLogo || DefaultLightLogo,
        customCardImage: logos.companyCardImage || "",
        platformCardImage: platform?.logos?.cardImage || "",
        favicon: logos.favicon || "",
      };
      setLogos(nextLogos);
      writeBrandingLogosCache(user?.id, nextLogos);
      setIsBrandingFetched(true);
      return true;
    } catch (error) {
      const newer = newerRead(seq);
      if (newer) return newer;
      console.error("Error fetching branding:", error);
      // Keep defaults on error
      setIsBrandingFetched(true);
      return false;
    }
  };

  const fetchBranding = (): Promise<boolean> => {
    const seq = ++changes.current;
    const done = readBranding(seq);
    latestRead.current = { seq, done };
    return done;
  };

  useEffect(() => {
    if (isLoading) return;
    setIsBrandingFetched(false);
    setIsThemeReady(false);
    // Clear before fetching: a failed fetch after a user/company switch must
    // not leave the previous identity's branding applied.
    setActiveBrandColors(null);
    setPlatform(DEFAULT_PLATFORM);
    // Logos reset with the colours rather than persisting through the fetch.
    // This identity's cache is a hit only for the identity that wrote it, so a
    // switch shows nothing until the fetch answers, and a failed fetch leaves
    // nothing of the previous tenant behind.
    setLogos(logosFromCache(readBrandingLogosCache<Partial<Logos>>(user?.id)));
    fetchBranding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, user?.id]);

  // ============================================
  //  MARK READY
  // ============================================

  useEffect(() => {
    if (!isBrandingFetched) return;
    setIsThemeReady(true);
  }, [isBrandingFetched]);

  // ============================================
  //  APPLY FAVICON
  // ============================================

  useEffect(() => {
    if (!logos.favicon) return;
    const head = document.head;
    // Remove every existing icon link so old hrefs don't win the resolution
    // order (browsers pick the last matching one, but some keep cached copies
    // of earlier entries; clearing makes the swap deterministic).
    head.querySelectorAll("link[rel~='icon']").forEach((node) => node.remove());
    const link = document.createElement("link");
    link.rel = "icon";
    link.href = logos.favicon;
    head.appendChild(link);
  }, [logos.favicon]);

  // ============================================
  //  UPDATE BRAND COLORS (for company settings)
  // ============================================

  const updateBrandColors = (colors: Partial<ThemeColors>) => {
    colorsWrittenAt.current = ++changes.current;
    setActiveBrandColors((prevColors) => ({
      ...(platform.brandColors || {}),
      ...prevColors,
      ...colors,
    }));
  };

  // ============================================
  //  RESET TO DEFAULT COLORS
  // ============================================

  const resetTheme = () => {
    colorsWrittenAt.current = ++changes.current;
    const platformColors = platform.brandColors;

    if (platformColors && Object.keys(platformColors).length > 0) {
      setActiveBrandColors(platformColors);
    } else {
      setActiveBrandColors(null);
    }
  };

  const refetchBranding = async () => {
    setIsBrandingFetched(false);
    return fetchBranding();
  };
  return (
    <BrandingContext.Provider
      value={{
        platform,
        brandColors: activeBrandColors,
        logos: scopedLogos,

        updateBrandColors,
        refetchBranding,
        resetTheme,

        isBrandingLoaded: isThemeReady,
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
};
